import { jwtDecode } from 'jwt-decode';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { registerTokenRenewer } from '../api/tokenBridge';
import { authUrl } from '../config/authUrls';

const AuthContext = createContext();
const suiteSSO = process.env.REACT_APP_SUITE_SSO === 'true';

/** Refresh when the access token is this close to expiry (or already past). */
const TOKEN_EXPIRY_THRESHOLD = 5 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [apiClient, setApiClient] = useState(null);
  const [restoring, setRestoring] = useState(true);

  // Single-flight: one refresh in flight at a time; concurrent callers share
  // it. Critical because the server rotates the refresh cookie once and
  // revokes the family on reuse — two parallel refreshes log the user out.
  const refreshInFlight = useRef(null);
  const tokenRef = useRef(null);
  const loggedOutRef = useRef(false);

  const isTokenExpired = useCallback((t) => {
    if (!t) return true;
    try {
      const decoded = jwtDecode(t);
      return decoded.exp * 1000 < Date.now() + TOKEN_EXPIRY_THRESHOLD;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  }, []);

  const applySession = useCallback((userData, authToken) => {
    loggedOutRef.current = false;
    tokenRef.current = authToken;
    setToken(authToken);
    setUser(userData);
  }, []);

  const login = applySession;

  const logout = useCallback(() => {
    loggedOutRef.current = true;
    fetch(suiteSSO ? '/auth/logout' : authUrl('/logout'), { method: 'POST', credentials: 'include' }).catch(
      () => {}
    );
    tokenRef.current = null;
    setUser(null);
    setToken(null);
    setApiClient(null);
  }, []);

  const clearSession = useCallback(() => {
    loggedOutRef.current = true;
    tokenRef.current = null;
    setUser(null);
    setToken(null);
    setApiClient(null);
  }, []);

  /**
   * Refresh the access token. Never called concurrently: in-flight callers
   * await the same promise. A failed refresh clears the session only when
   * the user was logged in (session restore failure is silent — the refresh
   * cookie is simply absent, which is the logged-out baseline).
   */
  const refreshToken = useCallback(async () => {
    if (refreshInFlight.current) {
      return refreshInFlight.current;
    }
    const wasLoggedIn = Boolean(tokenRef.current);
    refreshInFlight.current = (async () => {
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (tokenRef.current) {
          headers.Authorization = `Bearer ${tokenRef.current}`;
        }
        const response = await fetch(suiteSSO ? '/auth/session' : authUrl('/refresh'), {
          method: suiteSSO ? 'GET' : 'POST',
          headers,
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error(`Token refresh failed: ${response.status}`);
        }
        const data = await response.json();
        if (!data?.token) {
          throw new Error('Token refresh returned no token');
        }
        if (loggedOutRef.current) return null;
        applySession(data.user, data.token);
        return data.token;
      } catch (error) {
        if (wasLoggedIn) {
          console.error('Token refresh failed:', error);
          clearSession();
        }
        return null;
      } finally {
        refreshInFlight.current = null;
      }
    })();
    return refreshInFlight.current;
  }, [applySession, clearSession]);

  /** Token with fresh-enough guarantee; used by fetch on 401 and callers. */
  const getValidToken = useCallback(async () => {
    if (tokenRef.current && !isTokenExpired(tokenRef.current)) {
      return tokenRef.current;
    }
    return refreshToken();
  }, [isTokenExpired, refreshToken]);

  // Session restore on first load (httpOnly refresh cookie). StrictMode and
  // double-mounts are absorbed by single-flight; nothing to restore stays
  // silent so logged-out users never see refresh noise.
  useEffect(() => {
    let cancelled = false;
    refreshToken().finally(() => {
      if (!cancelled) setRestoring(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Proactive refresh: renew shortly before the access token expires so a
  // user returning after 20-30 idle minutes still holds a valid token.
  // Runs at most every minute, checks the decoded expiry each tick.
  useEffect(() => {
    if (!token) return undefined;
    const id = window.setInterval(() => {
      const current = tokenRef.current;
      if (!current || isTokenExpired(current)) {
        refreshToken();
      }
    }, 60_000);
    return () => window.clearInterval(id);
  }, [token, isTokenExpired, refreshToken]);

  // Fetch/SDK 401 bridge: renew once and hand back the new token.
  useEffect(() => {
    registerTokenRenewer(refreshToken);
    return () => registerTokenRenewer(null);
  }, [refreshToken]);

  const value = useMemo(
    () => ({
      user,
      token,
      apiClient,
      restoring,
      login,
      logout,
      getValidToken,
      refreshToken,
    }),
    [user, token, apiClient, restoring, login, logout, getValidToken, refreshToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
